import React, {useState, useEffect} from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {isInvalidURL} from '../../../components/forms/validators'
import {Input} from '../../../components/forms/inputs'
import {
  FREE_RESPONSE_QUESTION,
  MULTIPLE_CHOICE_QUESTION,
  BOOLEAN_QUESTION,
  REDACTED_COLUMN,
  TYPE_COLUMN,
  PARAPHRASE,
  QUESTION_COLUMN,
  BOOL_CORRECT_COLUMN,
  FRQ_CORRECT_COLUMN,
  FRQ_INCORRECT_COLUMN,
  MCQ_CORRECT_COLUMN,
  MCQ_INCORRECT_COLUMN,
} from '../../../services/types'
import {fetchEvaluationTasksHeaders} from '../../../services/evaluation'
import type {EvaluationStageProps} from './types'

const contains = (val: string, vals: string[]) => vals.some((v) => val.search(v) >= 0)

const guessType = (column: string) => {
  let task_type = null
  if (contains(column, ['frq', 'free response'])) {
    task_type = FREE_RESPONSE_QUESTION
  } else if (contains(column, ['mrq', 'multiple choice'])) {
    task_type = MULTIPLE_CHOICE_QUESTION
  } else if (contains(column, ['bool', 'true', 'yes'])) {
    task_type = BOOLEAN_QUESTION
  }

  if (column === 'redacted') {
    return REDACTED_COLUMN
  } else if (['type'].includes(column)) {
    return TYPE_COLUMN
  } else if (['question', 'text'].includes(column)) {
    return QUESTION_COLUMN
  } else if (contains(column, ['incorrect', 'wrong'])) {
    switch (task_type) {
      case FREE_RESPONSE_QUESTION:
        return FRQ_INCORRECT_COLUMN
      default:
        return MCQ_INCORRECT_COLUMN
    }
  } else if (contains(column, ['bool'])) {
    return BOOL_CORRECT_COLUMN
  } else if (contains(column, ['correct'])) {
    switch (task_type) {
      case FREE_RESPONSE_QUESTION:
        return FRQ_CORRECT_COLUMN
      case MULTIPLE_CHOICE_QUESTION:
        return BOOL_CORRECT_COLUMN
      default:
        return MCQ_CORRECT_COLUMN
    }
  } else if (contains(column, ['paraphrase'])) {
    return PARAPHRASE
  } else {
    return undefined
  }
}

const EvaluationTasksUrl = ({
  evaluation,
  onChange,
  index,
  setNextHandler,
  enableNext,
}: EvaluationStageProps) => {
  const [validate, setValidate] = useState(false)
  const [error, setError] = useState<string>()

  const isValid = (url: string | undefined) => (url ? isInvalidURL(url) : 'A valid URL is required')

  const checkTasksUrl = async () => {
    const valid = !!(evaluation.csv_url && !isInvalidURL(evaluation.csv_url))
    setValidate(!valid)
    enableNext(valid)

    if (!valid) return valid

    try {
      const {origin, pathname, search} = new URL(evaluation.csv_url || '')
      const {columns} = await fetchEvaluationTasksHeaders(origin + pathname + search)
      onChange(
        'columns_mapping',
        columns.reduce(
          (acc, col) => ({...acc, [col]: {columnType: guessType(col.toLowerCase())}}),
          {}
        )
      )
      return true
    } catch (err) {
      enableNext(false)
      setError(`${err}`)
    }
    return false
  }

  useEffect(() => {
    setNextHandler(checkTasksUrl)
    if (validate || error) {
      enableNext(!isValid(evaluation.csv_url))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, evaluation])

  return (
    <Stack spacing={2}>
      <Typography>
        Please provide an URL to a CSV file or Google Sheet with the tasks of the evaluation. In the
        case of CSV files, please make sure the first row is a header with the names of columns -
        this header will be used in the next step to configure what columns should be mapped to in
        the resulting tasks.
      </Typography>

      <Typography>
        Check <a href="/docs/api/#number-of-questions-to-complete">the docs</a> for more info, or
        see our <a href="/samples/eval_tasks.csv ">sample tasks csv</a> for an example of what the
        file should look like.
      </Typography>
      <Stack direction="row" spacing={2}>
        <Input
          label="Tasks CSV URL"
          defaultValue={evaluation?.csv_url}
          setter={(url: string) => onChange('csv_url', url)}
          validate={validate}
          validator={isValid}
          sx={{width: '100%'}}
        />
      </Stack>
      {error && <Typography color="error">Error: {error}</Typography>}
    </Stack>
  )
}

export default EvaluationTasksUrl
